import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid2,
  IconButton,
  useMediaQuery,
  Button,
} from "@mui/material";
import {
  FilterPayload,
  EducationalContentGroup,
  EducationalContentItem,
} from "../../utility/interfaces/educationLibraryInterfaces";
import { ArrowIcon } from "../../components/icon-components/ArrowIcon";
import BackgroundLayout from "../../components/custom-backgrounds/BackgroundLayout";
import styles from "./CategoryOverlay.module.scss";
import { useTranslation } from "react-i18next";
import MyLearningCard from "../learning-card/MyLearningCard";
import LearningFilter from "../learning-filters/LearningFilter";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useGetLearningLibraryContentApiQuery } from "../learning-library/services/GetLearningLibraryApiSlice";
import DataLoader from "../../components/custom-data-loader/DataLoader";
import EmptyComponent from "../../components/v2/custom-empty-error-component/EmptyComponent";
import { KeyboardArrowDownSharp } from "@mui/icons-material";
import LearningContentSkeleton from "./LearningContentSkeleton";
import {
  getContentDuration,
  getPatientRoute,
  handleExternalLinkClick,
} from "../../utility/functions/common-functions";
import {
  CONTENT_STATUS_COMPLETED_ID,
  EDUCATION_CONTENT_SORCE_ID,
  SAVE_CLICK_STATUS_ID,
} from "../../utility/constants/common-constants";
import { useLocation, useNavigate } from "react-router-dom";
import { useSaveContentClickMutation } from "../view-learning-content/services/SaveContentStatusAccessLogAPISlice";
import { useSaveContentStatusMutation } from "../view-learning-content/services/SaveContentStatusAPISlice";
import { setLearningContent } from "../view-learning-content/services/LearningContentDataSlice";
import ViewLearningContent from "../view-learning-content/ViewLearningContent";
import dayjs from "dayjs";

interface CategoryOverlayProps {
  categoryObject: EducationalContentGroup;
  onClose: () => void;
  filters: FilterPayload;
}

const CategoryOverlay: React.FC<CategoryOverlayProps> = ({
  categoryObject,
  onClose,
  filters,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const pendingStatuses = [
    t("inProgress"),
    t("pending"),
    t("taskPending"),
    t("notCompleted"),
    t("initiated"),
    t("notStarted"),
    "",
  ];

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState<FilterPayload>({
    ...filters,
    category: filters.category.includes(categoryObject.activitySubCategory)
      ? filters.category
      : [...filters.category, categoryObject.activitySubCategory],
  });

  // Safely access learningContent slice, defaulting to false
  const isViewLearningContentEnabled = useAppSelector(
    (state) =>
      state.learningContent?.navigationInfo?.isViewLearningContentEnabled
  );

  const {
    patientProfileInfo: { patientProfileId, userName: patientUserName },
  } = useAppSelector((state) => state.patientProfileDetails);

  const patientPreferredLang = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );

  const [saveContentClick] = useSaveContentClickMutation();
  const [contentStatus] = useSaveContentStatusMutation();

  const {
    data: filteredData,
    error,
    isLoading,
  } = useGetLearningLibraryContentApiQuery(
    {
      patientProfileId: patientProfileId,
      lang: patientPreferredLang,
      body: appliedFilters,
    },
    { skip: !filtersApplied }
  );

  const closeFilter = () => {
    setFilterOpen(false);
  };

  const toggleFilter = (open: boolean) => () => {
    setFilterOpen(open);
  };

  const handleApplyFilters = (filters: any) => {
    setAppliedFilters(filters);
    setFiltersApplied(true);
    setFilterOpen(false);
    window.scrollTo(0, 0);
  };

  const displayedContents: EducationalContentItem[] = filtersApplied
    ? filteredData?.educationlLibrary?.[0]?.contents ?? []
    : categoryObject.contents;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const showContentDetails = async (content: EducationalContentItem) => {
    const publishedDate = dayjs(content.publishDate);
    setFilterOpen(false);
    if (content?.educationalContentType === "URL") {
      if (content?.educationalContentURL) {
        window.open(
          content?.educationalContentURL,
          "_blank",
          "noopener,noreferrer"
        );
        if (content?.activityCompletionStatus !== t("completed")) {
          handleExternalLinkClick(
            content.casePlanMilestoneMappingId,
            CONTENT_STATUS_COMPLETED_ID,
            patientUserName || "Patient",
            patientProfileId,
            patientPreferredLang,
            dispatch,
            contentStatus
          );
        }
      }
    } else {
      dispatch(
        setLearningContent({
          type: content?.educationalContentType,
          category: content?.activitySubCategoryName,
          isNew: content?.isNew,
          videoUrl:
            content.educationalContentSourceId ===
            EDUCATION_CONTENT_SORCE_ID.AGILITY_CMS_ID
              ? content.educationalContentVideoURL
              : content.educationalContentURL ?? "",
          fullHtml: content?.educationalContentBody,
          title: content?.title,
          summarry: content?.description,
          casePlanMilestoneMappingId: content?.casePlanMilestoneMappingId,
          activityCompletionStatus: content?.activityCompletionStatus,
          patientProfileId,
          lang: patientPreferredLang,
          educationalContentSourceId: content?.educationalContentSourceId,
          educationalContentIdentifier: content?.educationalContentId.toString(),
          audioUrl: content.educationalContentAudioURL || "",
          activityPoints: content?.rewardPoint,
          thumbnail: content?.thumbnailURL,
          length: getContentDuration(
            content?.length,
            content?.videoDuration
          ),
          publisherName: content.articleAuthor
            ? `${content.articleAuthor.name}, ${content.articleAuthor.credentials}`
            : "",
          publishedDate:
            `${t("viewLearningContent.published", {
              date: publishedDate.format("MMM D, YYYY"),
            })}` || "",
          navigationInfo: {
            isViewLearningContentEnabled: true,
            arrivedFrom: location.pathname,
          },
        })
      );

      navigate(getPatientRoute(patientProfileId, "/learning"));
    }

    try {
      const body = {
        casePlanMilestoneMappingId: content?.casePlanMilestoneMappingId || "",
        patientProfileId: patientProfileId,
        planId: 1,
        caseId: 1,
        milestoneId: 1,
        contentIdentifier: content?.educationalContentId.toString(),
        contentWatchedDurationInMins: 1,
        action: SAVE_CLICK_STATUS_ID,
        educationalContentSourceId: content?.educationalContentSourceId,
      };

      await saveContentClick({
        body,
        patientPreferredLang,
      }).unwrap();
    } catch (err: any) {
      console.log(err, "saveContentClick");
    }
  };

  return isViewLearningContentEnabled ? (
    <Box>
      <ViewLearningContent />
    </Box>
  ) : (
    <Box>
      <BackgroundLayout
        variant={
          isSmallScreen
            ? "learningCategoryImageMobile"
            : "learningCategoryImageDesktop"
        }
        height={{ xs: "10rem", sm: "10rem", md: "12.31rem" }}
        showFooter={true}
      >
        <Box
          className={styles.mainLayout}
          maxWidth={"996px"}
          minHeight={"1000px"}
        >
          <Box className={styles.headerSection}>
            <IconButton
              onClick={onClose}
              sx={{ position: "absolute", left: 0 }}
              aria-label="Back"
            >
              <ArrowIcon width={20} height={20} style={{ marginLeft: 0 }} />
            </IconButton>
            <Typography
              variant={isSmallScreen ? "h3" : "h1"}
              align="center"
              className={styles.categoryHeading}
            >
              {categoryObject.activitySubCategoryName}
            </Typography>
          </Box>

          <Box className={styles.resourceInfo}>
            {displayedContents.length > 0 ? (
              <Typography variant="body1" fontWeight={500}>
                {displayedContents.length}{" "}
                {displayedContents.length > 1
                  ? t("educationContent.resources")
                  : t("educationContent.resource")}
              </Typography>
            ) : (
              <Box />
            )}

            <Button
              ref={anchorRef}
              variant="outlined"
              onClick={toggleFilter(true)}
              className={styles.filterButton}
              endIcon={
                <KeyboardArrowDownSharp
                  sx={{
                    color: "black",
                    fontSize: "20px",
                  }}
                />
              }
            >
              {t("educationContent.filterBy")}
            </Button>

            <LearningFilter
              open={filterOpen}
              anchorEl={anchorRef.current}
              onClose={closeFilter}
              onApply={handleApplyFilters}
              defaultFilters={appliedFilters}
            />
          </Box>
          <DataLoader
            isLoading={filtersApplied && isLoading}
            error={filtersApplied && error}
            data={displayedContents}
            loadingComponent={<LearningContentSkeleton />}
            errorComponent={
              <EmptyComponent
                message={t("anErrorOccurred")}
                textColor="error"
              />
            }
            emptyComponent={<EmptyComponent message={t("noContentFound")} />}
          >
            {(contents: EducationalContentItem[]) => (
              <Grid2
                container
                spacing={2}
              >
                {contents.map((educationContent, index) => {
                const duration = getContentDuration(
                  educationContent.length,
                  educationContent.videoDuration
                );
                return (
                  <Grid2
                    key={index}
                    size={{ xs: 12, sm: 12, md: 4 }}
                  >
                    <MyLearningCard
                      imageSrc={educationContent?.thumbnailURL}
                      imageAlt={educationContent?.title}
                      isNew={educationContent?.isNew}
                      duration={duration}
                      title={educationContent?.title}
                      width="100%"
                      height="250px"
                      contentType={educationContent?.educationalContentType}
                      activityPoints={educationContent?.rewardPoint}
                      changeHorizontalLayout={true}
                      hasAudioLink={
                        !!educationContent?.educationalContentAudioURL
                      }
                      showButton={false}
                      isCompleted={
                        !pendingStatuses.includes(
                          educationContent.activityCompletionStatus
                        )
                      }
                      activityCompletionStatus={
                        educationContent.activityCompletionStatus
                      }
                      onButtonClick={() => showContentDetails(educationContent)}
                    />
                  </Grid2>
                ) } 
              )}
              </Grid2>
            )}
          </DataLoader>
        </Box>
      </BackgroundLayout>
    </Box>
  );
};

export default CategoryOverlay;
